import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

import { RootState } from '@/store';
import { API_ENDPOINTS, API_SLICE, BASE_URL } from '@/utils/constants/apiEndpoints.ts';

import { AddDiagnosisCodeRequest, ApiBillableDto, ApiBillingDto, Billing } from '@/types/billing';

export const billingApiSlice = createApi({
  reducerPath: 'billingApi',
  baseQuery: fetchBaseQuery({
    baseUrl: BASE_URL,
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as RootState).auth.token;
      if (token) {
        headers.set('authorization', `Bearer ${token}`);
      }
      return headers;
    }
  }),
  tagTypes: [API_SLICE.Billing],
  endpoints: (builder) => ({
    getBilling: builder.query<Billing, number>({
      query: (reportId) => ({
        url: API_ENDPOINTS.BILLING.FIND_BY_REPORT_ID(reportId),
        method: 'GET'
      }),
      providesTags: (_result, _error, id) => [{ type: API_SLICE.Billing, id }]
    }),
    updateBilling: builder.mutation<Billing, ApiBillingDto>({
      query: ({ billingId, data }) => ({
        url: API_ENDPOINTS.BILLING.UPDATE_BILLING(billingId),
        method: 'PATCH',
        body: data
      }),
      invalidatesTags: (_result, _error, args) => [
        { type: API_SLICE.Billing, id: args.data.transmissionReportId }
      ]
    }),
    updateIsBillable: builder.mutation<Billing, ApiBillableDto>({
      query: ({ billingId, data }) => ({
        url: API_ENDPOINTS.BILLING.UPDATE_IS_BILLABLE(billingId),
        method: 'PATCH',
        body: data
      }),
      invalidatesTags: (_result, _error, args) => [
        { type: API_SLICE.Billing, id: args.data.transmissionReportId }
      ]
    }),
    addDiagnosisCode: builder.mutation<Billing, AddDiagnosisCodeRequest>({
      query: ({ reportId, billingId, diagnosisCode }) => ({
        url: API_ENDPOINTS.BILLING.ADD_DIAGNOSIS_CODE(reportId, billingId),
        method: 'PATCH',
        body: { diagnosisCode }
      }),
      invalidatesTags: (_result, _error, args) => [{ type: API_SLICE.Billing, id: args.reportId }]
    })
    // Add other endpoints here.
  })
});

export const {
  useGetBillingQuery,
  useUpdateBillingMutation,
  useUpdateIsBillableMutation,
  useAddDiagnosisCodeMutation
} = billingApiSlice;
